import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, FileText, Lectern, Users } from "lucide-react";
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SectionLabelTypography } from "./Community.styles";
import { CommunityTopicsTabs } from "./CommunityTopicsTabs.component";
import {
  CreateContentCommunity,
  GridMain,
  Spinner,
  TopicCard,
  TopicCardPost,
  TopicCardShare,
  WidgetDrawer,
  CommunityClusterSelect
} from "../../components";
import { useAnnouncement } from "../../components/Announcement/Announcement.component";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { POST_TYPE_FILTER } from "../../consts";
import { UserNavigationContext } from "../../context/UserNavigationContext";
import { useInfiniteFeedQuery } from "../../queries/community";
import type { CommunityTopicResponse } from "../../queries/community.types";
import { useAuthStore, useCurrentCommunityCluster } from "../../stores";
import type { PostTypeFilter } from "../../types";

export function Community() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { announce } = useAnnouncement();
  const [searchParams] = useSearchParams();
  const sharedId = searchParams.get("scrollTo");
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  useEffect(() => {
    setSpinnerLoginVisible(false);
  }, [setSpinnerLoginVisible]);

  useEffect(() => {
    announce( t("announcement_community_page"));
  }, []);

  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [allTopics, setAllTopics] = useState<CommunityTopicResponse[]>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const itemsRef = useRef<HTMLDivElement[] | null[]>([]);

  const enableCommunityFlag = useAuthStore(state => state.corporateInfo?.enable_community_flag);
  const funcCommunity = useAuthStore(state => state.smartConfiguration?.funcCommunity);
  const [selectedFilter, setSelectedFilter] = useState<PostTypeFilter[]>([]);
  const { setNavigation } = useContext(UserNavigationContext);

  useEffect(() => {
    setNavigation && setNavigation([]);
    setNavigation && setNavigation(["/community"]);
  }, [location.pathname]);
  const { isLoading: isClustersLoading, currentCluster } = useCurrentCommunityCluster();
  const {
    data: results,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useInfiniteFeedQuery({
    postType:selectedFilter?.toString(),
    sharedId: sharedId ?? "",
    topics: selectedTopics,
    enabled: !!currentCluster,
    onError: (err: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((err as any)?.response?.status === 404) {
        announce(t("no_result_community_filters"));
      }
    }
  });

  useEffect(() => {
    if (results && results.pages?.[0]?.topic) {
      setAllTopics(results.pages?.[0]?.topic); //to prevent blinking effect
    }
  }, [results]);

  useEffect(()=> {
    if(sharedId && itemsRef && itemsRef.current.length > 0 ){
      const topciShareFirst = itemsRef?.current[0];
      topciShareFirst?.scrollIntoView({ behavior: "smooth" });
    }
  }, [sharedId, results]);


  const pagesContainData = results?.pages && results.pages[0]?.data && results.pages[0].data.length > 0;

  const filterOptions = useMemo(() => {
    const opts = [];
    if(currentCluster?.is_post_enabled === true){
      opts.push({
        children: t("post"),
        value: POST_TYPE_FILTER.FREE,
        icon: FileText
      });
    }
    if(currentCluster?.is_share_enabled === true){
      opts.push({
        children: t("shared"),
        value:POST_TYPE_FILTER.SHARED,
        icon: Users
      });
    }
    if(currentCluster?.is_talk_enabled === true){
      opts.push({
        children: t("talk"),
        value: POST_TYPE_FILTER.DISCUSSION,
        icon: Lectern
      });
    }
    return opts;
  }, [currentCluster?.is_post_enabled, currentCluster?.is_share_enabled, currentCluster?.is_talk_enabled, t]);


  const isInitializing =
    enableCommunityFlag === undefined ||
    funcCommunity === undefined ||
    isClustersLoading ||
    (enableCommunityFlag !== false && funcCommunity !== false && !currentCluster);

  const isCommunityDisabled =
    enableCommunityFlag === false ||
    (enableCommunityFlag === true && funcCommunity === false) ||
    (currentCluster != null &&
      currentCluster.is_post_enabled === false &&
      currentCluster.is_share_enabled === false &&
      currentCluster.is_talk_enabled === false
    );

  if (isInitializing) {
    return (
      <GridMain justifyContent={ "center" }>
        <Spinner
          size={ 80 }
          thickness={ 3 }
          sx={ { color: theme.customColors.accentVariantA } }
        />
      </GridMain>
    );
  }

  return isCommunityDisabled
    ? (
      <GridMain justifyContent={ "center" }>
        <Typography
          padding={ "42px 80px" }
          textAlign={ "center" }
        >
          { t("no_community_available") }
        </Typography>
      </GridMain>
    ) : (
      <GridMain
        component="div"
        role="none"
        padding={ !isMobile ? "0 2.5rem" : "0 1rem" }
        gap={ !isMobile ? "36px" : "0" }
        sx={ {
          flexFlow: "unset !important",
          maxWidth: "939px",
          mx:  "auto"
        } }
      >
        <Grid
          container
          item
          component="article"
          aria-label={ t("topic_list") }
          flexDirection="column"
          flexGrow={ 1 }
          sx={ {

            [theme.breakpoints.down("sm")]: {
              padding: "0"
              // width: "100%"
            }
          } }
        >
          <Stack
            gap={ "24px" }
            paddingY={ "64px" }
          >
            <CommunityClusterSelect />
            { currentCluster?.is_post_enabled && <CreateContentCommunity
              selectedFilter={ selectedFilter?.toString() || null }
              selectedTopic={ selectedTopics?.join(",") || null }
            /> }

            <Stack
              direction={ "column" }
              gap={ "16px" }
              alignItems={ "start" }
            >
              <SectionLabelTypography>
                { t("filter") }
              </SectionLabelTypography>
              <Stack
                direction={ "row" }
                gap={ "0.5rem" }
                width={ "100%" }
                flexWrap={ "wrap" }
              >
                { filterOptions.map((o) => {
                  const Icon = o.icon;
                  const isSelected = selectedFilter.includes(o.value as PostTypeFilter);
                  return (
                    <ButtonDigited
                      key={ `filter_option_${o.value}` }
                      type={ "button" }
                      typology={ "secondary" }
                      buttonSize={ "sm" }
                      onClick={ () => {
                        if(selectedFilter.includes(o.value as PostTypeFilter)) {
                          setSelectedFilter((current) => current.filter((v) => v !== o.value));
                        } else {
                          setSelectedFilter((current) => [...current, o.value as PostTypeFilter]);
                        }
                      } }
                      sx={ {
                        height: "2rem",
                        display: "flex",
                        gap: "0.5rem",
                        padding: 0,
                        borderColor: `${isSelected ? theme.customColors.borderPrimaryCta : theme.customColors.textPrimary} !important`,
                        "&.Mui-focusVisible": {
                          outline: `2px solid ${theme.customColors.borderPrimaryCta}`,
                          outlineOffset: "2px",
                          borderRadius: "2px"
                        }
                      } }
                    >
                      <>
                        <Icon fontSize="1rem" width="1rem" />
                        <Typography
                          color={ theme.customColors.textPrimary }
                          fontSize={ "0.75rem" }
                          fontWeight={ 600 }
                        >
                          { o.children }
                        </Typography>
                      </>
                    </ButtonDigited>
                  );
                }) }
                { !isMobile && (
                  <Box
                    sx={ {
                      flex: 1,
                      height: "1px",
                      backgroundColor: theme.palette.divider,
                      opacity: 0.5,
                      alignSelf: "center",
                      marginLeft: "20px"
                    } }
                  />
                ) }
              </Stack>
            </Stack>

            {
              allTopics?.length > 0 ? (
                <Box
                  sx={ {
                    display: "flex",
                    flexDirection: "column",
                    width:"100% !important",
                    maxWidth:"100%",
                    padding: "1rem 0rem",
                    gap: "0.5rem"
                  } }
                >
                  <SectionLabelTypography>
                    { t("your_topic") }
                  </SectionLabelTypography>

                  <CommunityTopicsTabs
                    topics={ allTopics }
                    selectedTopics={ selectedTopics }
                    onTopicChange={ (topicCode) => {
                      setSelectedTopics((prev) => {
                        const newState = [ ...prev ];
                        const topicIndex = newState.indexOf(topicCode);
                        const topicName = allTopics.find(t => t.code === topicCode)?.name ?? "";
                        if (topicIndex >= 0) {
                          newState.splice(topicIndex, 1);
                          announce(t("topic_communty_removed", { name: topicName }));
                        } else {
                          newState.push(topicCode);
                          announce(t("topic_communty_applied", { name: topicName }));
                        }
                        return newState.sort();
                      });
                    } }
                  />

                </Box>
              ) : null
            }
            {
              isLoading && (
                <Spinner
                  size={ 80 }
                  thickness={ 3 }
                  sx={ { color: theme.customColors.accentVariantA } }
                />
              )
            }
            {
              isError ? (
                <Typography
                  padding={ "42px 80px" }
                  textAlign={ "center" }
                >
                  { t("error_occurred") }
                </Typography>
              ) : null
            }
            {
              isLoading || isError ? null : pagesContainData ? (
                results.pages.map((page) => (
                  page?.data?.map((discussion) => {
                    const ownerUser =
                      page?.enrichedInformation?.users?.find(user =>
                        user.owner_id === discussion.owner_id
                      );

                    return (
                      <>
                        {
                          discussion?.post_type === "free" ? (
                            <TopicCardPost
                              attachments={ discussion.attachments }
                              body={ discussion.body }
                              changed={ discussion.changed }
                              commentsNumber={ discussion.comment_count }
                              created={  discussion.created  }
                              id={ discussion.id }
                              key={ `topic-${discussion.id}` }
                              likes={ discussion.likes }
                              ownerName={ `${ownerUser?.name} ${ownerUser?.surname}` || "" }
                              ownerImage={ ownerUser?.profileImageUrl || "" }
                              selectedFilter={ selectedFilter?.toString() || null  }
                              selectedTopic={ selectedTopics?.join(",") || null }
                              topicOwner={ discussion.owner_id }
                            />
                          ) : null
                        }
                        {
                          discussion?.post_type === "shared" ?  (
                            <TopicCardShare
                              selectedFilter={ selectedFilter?.toString() || null  }
                              selectedTopic={ selectedTopics?.join(",") || null }
                              key={ `topic-${discussion.id}` }
                              body={ discussion.body }
                              changed={ discussion.changed }
                              commentsNumber={ discussion.comment_count }
                              created={ discussion.created }
                              id={ discussion.id }
                              learningObjectTypology={ discussion.lo_type }
                              ownerImage={ ownerUser?.profileImageUrl || "" }
                              likes={ discussion.likes }
                              ownerName={ `${ownerUser?.name} ${ownerUser?.surname}` || "" }
                              sharedLo={ page?.enrichedInformation?.los?.find(lo => lo.lo_id === discussion.lo_id) }
                              topicOwner={ discussion.owner_id }
                            />
                          ) : null
                        }
                        {
                          (
                            discussion?.post_type !== "free"
                            && discussion?.post_type !== "shared"
                          )
                            ? (
                              <TopicCard
                                key={ `topic-${discussion.id}` }
                                body={ discussion.body }
                                topic={ discussion?.topic?.map((t) => t.name) }
                                commentsNumber={ discussion.comment_count }
                                created={ discussion.publishDate }
                                id={ discussion.id }
                                ownerImage={ ownerUser?.profileImageUrl || "" }
                                likes={ discussion.likes }
                                ownerName={ `${ownerUser?.name} ${ownerUser?.surname}` || "" }
                                title={  discussion.title }
                                subtitle={ discussion.subtitle }
                                image={ discussion.attachments.image?.[0].content }
                                attachments={ discussion.attachments }
                                selectedFilter={ selectedFilter?.toString() || null  }
                                selectedTopic={ selectedTopics?.join(",") || null }
                                topicOwner={ discussion.owner_id }
                              />
                            ) : null
                        }
                      </>
                    );
                  })
                ))
              ) : (
                <Grid
                  alignItems={ "center" }
                  display={ "flex" }
                  flexDirection={ "column" }
                  justifyContent={ "center" }
                  gap={ 2 }

                >
                  <Typography
                    padding={ "42px 80px" }
                    textAlign={ "center" }
                  >
                    { t("no_posts_available") }
                  </Typography>
                  <Button
                    sx={ { color:theme.customColors.accentVariantA } }
                    onClick={ ()=> {
                      setSelectedTopics([]);
                      setSelectedFilter([]);
                    } }
                  >{ t("remove_filters") }</Button>
                </Grid>
              )
            }
            {
              isFetchingNextPage ? (
                <Box
                  sx={ {
                    display: "flex",
                    justifyContent: "center",
                    margin: "0 auto",
                    marginBottom: "1.5rem"
                  } }
                >
                  <Spinner
                    size={ 40 }
                    thickness={ 3 }
                    sx={ { color: theme.customColors.accentVariantA } }
                  />
                </Box>
              ) : hasNextPage ? (
                <Stack
                  flexDirection={ "row" }
                  width={ "100%" }
                  justifyContent={ "center" }
                >

                  <ButtonDigited
                    typology="primary"
                    disabled={ !hasNextPage }
                    onClick={ () => fetchNextPage() }
                    sx={ {
                      color: theme.customColors.backgroundPrimaryCta,
                      marginBottom:"1.5rem"
                    } }
                  >
                    <>
                      { t("load_more") }
                      <ChevronDown
                        size="1rem"
                      />
                    </>
                  </ButtonDigited>
                </Stack>
              ) : null
            }
          </Stack>
        </Grid>

        <WidgetDrawer />
      </GridMain>
    );
}
